// Expense Management — employee experience (AMAS pilot). Real API-backed
// flows: draft reports, items, receipt upload, policy warnings, submit,
// withdraw, status timeline and reimbursement status. No mock data.
import { formatDate } from '@/lib/orgFormat';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Plus, Receipt, Loader2, ChevronLeft, Upload, Trash2, Send, Undo2, AlertTriangle, FileText, Pencil, Ban
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/features/shared/components/ui/card';
import { Button } from '@/features/shared/components/ui/button';
import { getApiErrorMessage } from '@/lib/apiError';
import { MyImprestsPanel } from '@/features/expenses/ImprestPanels';
import { listBranches, type Branch } from '@/lib/hrApi';
import { withBranch, readBranchId, readBranchLabel } from '@/features/expenses/expenseLocation';
import { usePrompt, useConfirm } from '@/features/shared/components/confirm/ConfirmProvider';
import { CancelWithReasonDialog } from '@/features/shared/components/CancelWithReasonDialog';
import {
  listMyReports, createReport, getReport, addItem, updateItem, deleteItem, submitReport, withdrawReport,
  cancelReport, discardReport, canCancelReport, cancelReasonRequired, canDiscardReport,
  EXPENSE_CANCEL_REASONS,
  uploadReceipt, receiptDownload, listCategories, updateReceiptEvidence, declareMissingReceipt,
  listAdvances, requestAdvance, advanceAction, extractReceipt,
  type ExpenseReport, type ReportDetail, type ExpenseCategory, type PolicyFinding, type ItemInput,
  type ExpenseAdvance, type EvidenceUpdate,
} from '@/lib/expensesApi';
import { useUnsavedGuard, usePendingGuard } from '@/features/shared/components/recovery/useFormGuards';
import { FinanceStageNotice } from './FinanceStageNotice';

const STATUS_STYLES: Record<string, string> = {
  DRAFT: 'bg-muted text-muted-foreground',
  SUBMITTED: 'bg-amber-100 text-amber-700 dark:bg-amber-500/[0.15] dark:text-amber-300',
  MANAGER_REVIEW: 'bg-amber-100 text-amber-700 dark:bg-amber-500/[0.15] dark:text-amber-300',
  FINANCE_REVIEW: 'bg-blue-100 text-blue-700 dark:bg-blue-500/[0.15] dark:text-blue-300',
  CHANGES_REQUESTED: 'bg-orange-100 text-orange-700 dark:bg-orange-500/[0.15] dark:text-orange-300',
  APPROVED: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/[0.15] dark:text-emerald-300',
  SCHEDULED: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/[0.15] dark:text-emerald-300',
  REIMBURSED: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/[0.15] dark:text-emerald-300',
  REJECTED: 'bg-red-100 text-red-700 dark:bg-red-500/[0.15] dark:text-red-300',
  WITHDRAWN: 'bg-muted text-muted-foreground',
  PAYMENT_FAILED: 'bg-red-100 text-red-700 dark:bg-red-500/[0.15] dark:text-red-300',
  CANCELLED: 'bg-muted text-muted-foreground',
};

export const StatusChip: React.FC<{ status: string }> = ({ status }) => {
  const { t } = useTranslation(['expenses']);
  return (
    <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${STATUS_STYLES[status] || 'bg-muted text-muted-foreground'}`}>
      {/* The word alone ("Approved") announces without saying WHAT it describes,
          and the colour that carries the same meaning is invisible to a screen
          reader. A visually-hidden prefix makes it "Status: Approved" out of
          context, at no cost to the visual design. */}
      <span className="sr-only">{t('expenses:status_prefix', { defaultValue: 'Status:' })} </span>
      {t(`expenses:status.${status}`, { defaultValue: status.replaceAll('_', ' ') })}
    </span>
  );
};

export const FindingsList: React.FC<{ findings: PolicyFinding[] }> = ({ findings }) => {
  if (!findings.length) return null;
  return (
    <div className="space-y-1.5">
      {findings.map((f, i) => (
        <div key={i} className={`flex items-start gap-2 text-sm rounded-lg px-3 py-2 ${
          f.outcome === 'BLOCKED' ? 'bg-red-50 text-red-700 dark:bg-red-500/[0.1] dark:text-red-300'
            : 'bg-amber-50 text-amber-700 dark:bg-amber-500/[0.1] dark:text-amber-300'}`}>
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{f.message}</span>
        </div>
      ))}
    </div>
  );
};

const input = 'w-full h-10 px-3 rounded-lg border border-border bg-background text-foreground text-sm focus:border-orange-400 focus:outline-none';
const label = 'block text-sm font-medium text-foreground mb-1';

// Receipt evidence editor (receipt-first model): capture fiscal details for a
// TZ EFD/VFD receipt (or classify paper/mobile-money evidence). Saving re-runs
// the country pack and reports the compliance verdict.
const EVIDENCE_TYPES = [
  ['fiscal_receipt', 'Fiscal receipt (EFD/VFD)'],
  ['supplier_invoice', 'Supplier invoice'],
  ['printed_receipt', 'Printed receipt'],
  ['handwritten_receipt', 'Handwritten receipt'],
  ['mobile_money_confirmation', 'Mobile money confirmation'],
  ['bank_confirmation', 'Bank confirmation'],
  ['electronic_receipt', 'Electronic receipt / PDF'],
  ['other', 'Other evidence'],
] as const;

export const ReceiptEvidenceEditor: React.FC<{ receiptId: string; onDone: () => void }> = ({ receiptId, onDone }) => {
  const { t } = useTranslation(['expenses']);
  const [docType, setDocType] = useState('fiscal_receipt');
  const [fiscalNo, setFiscalNo] = useState('');
  const [tin, setTin] = useState('');
  const [total, setTotal] = useState('');
  const [saving, setSaving] = useState(false);
  // Real expense ENTRY, not configuration: someone has typed a fiscal number
  // off a paper receipt they may no longer be holding. Any entered value
  // counts as unsaved — there is no separate dirty flag here, so the fields
  // themselves are the truth.
  useUnsavedGuard(
    Boolean(fiscalNo || tin || total),
    `receipt-evidence:${receiptId}`,
    'receipt details',
  );
  usePendingGuard(saving, `receipt-evidence:${receiptId}`, 'receipt details');
  return (
    <div className="rounded-lg border border-border bg-muted/30 p-3 space-y-2 w-full">
      <div className="grid sm:grid-cols-2 gap-2">
        <select className={input} value={docType} onChange={(e) => setDocType(e.target.value)}>
          {EVIDENCE_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <input className={input} placeholder={t('expenses:evidence.fiscal_no', { defaultValue: 'Receipt / invoice number' })}
          value={fiscalNo} onChange={(e) => setFiscalNo(e.target.value)} />
        <input className={input} placeholder={t('expenses:evidence.tin', { defaultValue: 'Supplier TIN' })}
          value={tin} onChange={(e) => setTin(e.target.value)} />
        <input className={input} inputMode="decimal" placeholder={t('expenses:evidence.total', { defaultValue: 'Total on receipt' })}
          value={total} onChange={(e) => setTotal(e.target.value)} />
      </div>
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="outline" onClick={onDone}>{t('expenses:buttons.cancel', { defaultValue: 'Cancel' })}</Button>
        <Button size="sm" className="bg-orange-500 hover:bg-orange-600 text-white" disabled={saving}
          onClick={async () => {
            setSaving(true);
            try {
              const upd: EvidenceUpdate = { documentType: docType, countryCode: 'TZ' };
              if (fiscalNo.trim()) upd.fiscalDocumentNumber = fiscalNo.trim();
              if (tin.trim()) upd.supplierTaxId = tin.trim();
              if (total.trim()) { upd.totalAmount = total.trim(); upd.currency = 'TZS'; }
              const res = await updateReceiptEvidence(receiptId, upd);
              toast.success(t('expenses:evidence.saved', {
                defaultValue: 'Evidence saved — compliance: {{status}}', status: res.complianceStatus,
              }));
              if (res.notes?.length) toast(res.notes.join(' · '), { icon: 'ℹ️' });
              onDone();
            } catch (e) {
              toast.error(getApiErrorMessage(e, 'Could not save evidence details.'));
            } finally {
              setSaving(false);
            }
          }}>
          {t('expenses:evidence.save', { defaultValue: 'Save evidence' })}
        </Button>
      </div>
    </div>
  );
};

// Employee cash advances: request, track, record returned cash.
export const AdvancesPanel: React.FC<{ finance?: boolean; onAction?: (a: ExpenseAdvance) => React.ReactNode }> = ({ finance = false, onAction }) => {
  const { t } = useTranslation(['expenses']);
  const promptText = usePrompt();
  const [advances, setAdvances] = useState<ExpenseAdvance[]>([]);
  const [purpose, setPurpose] = useState('');
  const [amount, setAmount] = useState('');
  const load = useCallback(() => { listAdvances().then(setAdvances).catch(() => setAdvances([])); }, []);
  useEffect(load, [load]);
  return (
    <Card>
      <CardHeader><CardTitle className="text-base">{t('expenses:advances.title', { defaultValue: 'Cash advances' })}</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        {!finance && (
          <div className="flex gap-2 flex-wrap">
            <input className={`${input} flex-1 min-w-[160px]`} placeholder={t('expenses:advances.purpose', { defaultValue: 'Purpose (e.g. Mwanza trip float)' })}
              value={purpose} onChange={(e) => setPurpose(e.target.value)} />
            <input className={`${input} w-36`} inputMode="decimal" placeholder="100000" value={amount} onChange={(e) => setAmount(e.target.value)} />
            <Button size="sm" variant="outline" disabled={!purpose.trim() || !amount.trim()}
              onClick={() => requestAdvance(purpose.trim(), amount.trim())
                .then(() => { setPurpose(''); setAmount(''); load(); toast.success(t('expenses:advances.requested', { defaultValue: 'Advance requested.' })); })
                .catch((e) => toast.error(getApiErrorMessage(e, 'Could not request the advance.')))}>
              {t('expenses:advances.request', { defaultValue: 'Request' })}
            </Button>
          </div>
        )}
        {advances.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('expenses:advances.empty', { defaultValue: 'No cash advances.' })}</p>
        ) : (
          <ul className="divide-y divide-border">
            {advances.map((a) => (
              <li key={a.id} className="py-2.5 flex items-center gap-3 flex-wrap">
                <div className="flex-1 min-w-[160px]">
                  <p className="text-sm font-medium text-foreground">{a.purpose}</p>
                  <p className="text-xs text-muted-foreground">
                    {a.amount} {a.currency} · {t('expenses:advances.spent', { defaultValue: 'spent' })} {a.amountSpent} · {t('expenses:advances.returned', { defaultValue: 'returned' })} {a.amountReturned}
                  </p>
                </div>
                <StatusChip status={a.status} />
                {!finance && (a.status === 'ISSUED' || a.status === 'PARTIALLY_RECONCILED') && (
                  <Button size="sm" variant="outline"
                    onClick={async () => {
                      const v = await promptText({ title: t('expenses:advances.record_return', { defaultValue: 'Record return' }), label: t('expenses:advances.return_prompt', { defaultValue: 'Amount of cash you are returning:' }), required: true, confirmLabel: t('expenses:advances.record_return', { defaultValue: 'Record return' }) });
                      if (v?.trim()) void advanceAction(a.id, 'record-return', { returned: v.trim() })
                        .then(load).catch((e) => toast.error(getApiErrorMessage(e, 'Could not record the return.')));
                    }}>
                    {t('expenses:advances.record_return', { defaultValue: 'Record return' })}
                  </Button>
                )}
                {onAction?.(a)}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
};

// --- item form ---------------------------------------------------------------

/** Today from the LOCAL clock. toISOString() is UTC, so west of UTC in the
 *  evening it offers TOMORROW as the transaction date — the same bug that was
 *  rejecting same-day leave requests. */
const localToday = () => {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

const ItemForm: React.FC<{
  categories: ExpenseCategory[];
  baseCurrency: string;
  /** Present when EDITING an existing item; absent when adding a new one. The
   *  same form serves both so an edit cannot drift from what add accepts. */
  initial?: ItemInput;
  /** Branches, for "where did this happen?". Empty until they load, or when the
   *  company has none configured — in which case the field is not shown at all
   *  rather than offered as an empty dropdown. */
  branches: Branch[];
  onSave: (item: ItemInput, receipt: File | null) => Promise<void>;
  onCancel: () => void;
}> = ({ categories, baseCurrency, initial, branches, onSave, onCancel }) => {
  const { t } = useTranslation(['expenses']);
  const [categoryId, setCategoryId] = useState(initial?.categoryId || categories[0]?.id || '');
  const [txnDate, setTxnDate] = useState(initial?.txnDate?.slice(0, 10) || localToday());
  const [vendor, setVendor] = useState(initial?.vendor || '');
  const [description, setDescription] = useState(initial?.description || '');
  const [amount, setAmount] = useState(initial?.amount || '');
  const [currency, setCurrency] = useState(initial?.currency || baseCurrency);
  const [fxRate, setFxRate] = useState(initial?.fxRate || '');
  // Where the expense actually happened — Fern's rule, and not the employee's
  // home office. Finance binds each branch to a ledger Location once.
  const [branchId, setBranchId] = useState(readBranchId(initial?.allocation));
  const [vehicle, setVehicle] = useState('');
  const [odometer, setOdometer] = useState('');
  const [fuelQty, setFuelQty] = useState('');
  const [receipt, setReceipt] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);

  const cat = categories.find((c) => c.id === categoryId);
  const isFuel = cat?.code === 'fuel';
  const isOther = cat?.code === 'other';
  const needsFx = currency.toUpperCase() !== baseCurrency.toUpperCase();

  const save = async () => {
    if (!categoryId || !amount || !txnDate) {
      toast.error(t('expenses:errors.item_required', { defaultValue: 'Category, date and amount are required.' }));
      return;
    }
    if (isOther && !description.trim()) {
      toast.error(t('expenses:errors.other_explanation', { defaultValue: "'Other' expenses need an explanation." }));
      return;
    }
    setSaving(true);
    try {
      const logistics: Record<string, unknown> = {};
      if (vehicle.trim()) logistics.vehicle = vehicle.trim();
      if (odometer.trim()) logistics.odometer = odometer.trim();
      if (fuelQty.trim()) { logistics.fuel_qty = fuelQty.trim(); logistics.fuel_uom = 'L'; }
      await onSave({
        categoryId, txnDate, vendor: vendor.trim(), description: description.trim(),
        amount: amount.trim(), currency: currency.toUpperCase(),
        fxRate: needsFx ? fxRate.trim() : undefined,
        // Merge rather than replace: allocation is a shared bag, and a
        // department put there by another screen must survive.
        allocation: withBranch(initial?.allocation, branchId, branches),
        logistics: Object.keys(logistics).length ? logistics : undefined,
      }, receipt);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-xl border border-border p-4 space-y-3 bg-muted/30">
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <span className={label}>{t('expenses:fields.category', { defaultValue: 'Category' })}</span>
          <select className={input} value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
            {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div>
          <span className={label}>{t('expenses:fields.date', { defaultValue: 'Transaction date' })}</span>
          <input type="date" className={input} value={txnDate} onChange={(e) => setTxnDate(e.target.value)} />
        </div>
        <div>
          <span className={label}>{t('expenses:fields.vendor', { defaultValue: 'Merchant / vendor' })}</span>
          <input className={input} value={vendor} onChange={(e) => setVendor(e.target.value)} placeholder="Puma Energy" />
        </div>
        {/* Only offered when the company actually has branches — an empty
            dropdown reads as a broken required field. */}
        {branches.length > 0 && (
        <div>
          <span className={label}>
            {t('expenses:fields.location', { defaultValue: 'Where did this happen?' })}
          </span>
          <select
            className={input}
            value={branchId}
            onChange={(e) => setBranchId(e.target.value)}
            data-testid="expense-location"
          >
            <option value="">
              {t('expenses:fields.location_none', { defaultValue: 'Not specified' })}
            </option>
            {branches.map((b) => (
              <option key={b.id} value={b.id}>{readBranchLabel({ branch: b.id }, branches) || b.name}</option>
            ))}
          </select>
        </div>
        )}
        <div>
          <span className={label}>{t('expenses:fields.amount', { defaultValue: 'Amount' })}</span>
          <div className="flex gap-2">
            <input className={input} inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="45000" />
            <input className={`${input} w-24`} value={currency} maxLength={3} onChange={(e) => setCurrency(e.target.value.toUpperCase())} />
          </div>
        </div>
        {needsFx && (
          <div>
            <span className={label}>{t('expenses:fields.fx_rate', { defaultValue: 'Rate to {{cur}}', cur: baseCurrency })}</span>
            <input className={input} inputMode="decimal" value={fxRate} onChange={(e) => setFxRate(e.target.value)} placeholder="2600" />
          </div>
        )}
      </div>
      <div>
        <span className={label}>{t('expenses:fields.purpose', { defaultValue: 'Business purpose' })}{isOther ? ' *' : ''}</span>
        <input className={input} value={description} onChange={(e) => setDescription(e.target.value)}
          placeholder={t('expenses:fields.purpose_hint', { defaultValue: 'What was this for?' })} />
      </div>
      {isFuel && (
        <div className="grid sm:grid-cols-3 gap-3">
          <div>
            <span className={label}>{t('expenses:fields.vehicle', { defaultValue: 'Vehicle' })}</span>
            <input className={input} value={vehicle} onChange={(e) => setVehicle(e.target.value)} placeholder="T 123 ABC" />
          </div>
          <div>
            <span className={label}>{t('expenses:fields.odometer', { defaultValue: 'Odometer (km)' })}</span>
            <input className={input} inputMode="numeric" value={odometer} onChange={(e) => setOdometer(e.target.value)} />
          </div>
          <div>
            <span className={label}>{t('expenses:fields.fuel_qty', { defaultValue: 'Fuel (litres)' })}</span>
            <input className={input} inputMode="decimal" value={fuelQty} onChange={(e) => setFuelQty(e.target.value)} />
          </div>
        </div>
      )}
      <div>
        <span className={label}>{t('expenses:fields.receipt', { defaultValue: 'Receipt (photo or PDF)' })}</span>
        <input type="file" accept="image/*,application/pdf" className="text-sm"
          onChange={async (e) => {
            const f = e.target.files?.[0] ?? null;
            setReceipt(f);
            // OCR assist: SUGGESTS values with confidence — the user confirms
            // every field; failure or low confidence never blocks manual entry.
            if (f && f.type.startsWith('image/')) {
              const tid = toast.loading(t('expenses:ocr.reading', { defaultValue: 'Reading the receipt…' }));
              const res = await extractReceipt(f);
              toast.dismiss(tid);
              if (res.extracted && res.fields) {
                const c = res.confidence ?? {};
                const sure = (k: string) => (c[k] ?? 0) >= 0.6;
                if (res.fields.total_amount && sure('total_amount') && !amount) setAmount(res.fields.total_amount);
                if (res.fields.vendor && sure('vendor') && !vendor) setVendor(res.fields.vendor);
                if (res.fields.txn_date && sure('txn_date')) setTxnDate(res.fields.txn_date);
                if (res.fields.currency && sure('currency')) setCurrency(res.fields.currency);
                const unsure = Object.keys(c).filter((k) => (c[k] ?? 0) > 0 && (c[k] ?? 0) < 0.6 && res.fields?.[k]);
                toast.success(unsure.length
                  ? t('expenses:ocr.partial', { defaultValue: 'Suggested some fields — please double-check {{f}}.', f: unsure.join(', ') })
                  : t('expenses:ocr.done', { defaultValue: 'Suggested fields from the receipt — confirm before saving.' }));
              }
            }
          }} />
      </div>
      <div className="flex gap-2 justify-end">
        <Button variant="outline" size="sm" onClick={onCancel} disabled={saving}>
          {t('expenses:buttons.cancel', { defaultValue: 'Cancel' })}
        </Button>
        <Button size="sm" className="bg-orange-500 hover:bg-orange-600 text-white" onClick={save} disabled={saving}>
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Plus className="w-4 h-4 mr-1" />}
          {t('expenses:buttons.add_item', { defaultValue: 'Add expense' })}
        </Button>
      </div>
    </div>
  );
};

// --- report detail ------------------------------------------------------------

export const ReportDetailView: React.FC<{
  reportId: string;
  onBack: () => void;
  readOnly?: boolean;
  actions?: (detail: ReportDetail, refresh: () => void) => React.ReactNode;
  /**
   * A receipt whose evidence editor should be open when this view appears, so a
   * claim's evidence form can be reached by link instead of by clicking down
   * through list → claim → item → Evidence…. Ignored if the receipt is not on
   * this claim.
   */
  openReceiptId?: string;
}> = ({ reportId, onBack, readOnly = false, actions, openReceiptId }) => {
  const { t } = useTranslation(['expenses']);
  const promptText = usePrompt();
  const [detail, setDetail] = useState<ReportDetail | null>(null);
  const [categories, setCategories] = useState<ExpenseCategory[]>([]);
  // Branches answer "where did this happen?". Loaded separately and allowed to
  // fail quietly: a company with none, or an org service having a bad minute,
  // must not stop somebody filing an expense — the field simply does not appear.
  const [branches, setBranches] = useState<Branch[]>([]);
  const [loading, setLoading] = useState(true);
  const [showItemForm, setShowItemForm] = useState(false);
  const [findings, setFindings] = useState<PolicyFinding[]>([]);
  const [busy, setBusy] = useState(false);
  // null = no dialog. Withdraw and cancel are the same form with different
  // stakes, so one piece of state carries which one is open.
  const [stopMode, setStopMode] = useState<'withdraw' | 'cancel' | null>(null);
  const confirm = useConfirm();
  const [editingReceipt, setEditingReceipt] = useState<string | null>(openReceiptId ?? null);
  const [editingItem, setEditingItem] = useState<string | null>(null);

  const refresh = useCallback(() => {
    getReport(reportId).then(setDetail).catch((e) => toast.error(getApiErrorMessage(e, 'Could not load the report.')));
  }, [reportId]);

  // A failed load must END somewhere the person can read. It used to be a
  // toast while the view kept waiting for a report that was never coming —
  // a spinner forever (dev, 2026-10-21: a scoped finance officer's own draft
  // answered 404). The reason now stays on screen, with Retry and Back.
  const [loadError, setLoadError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let stopped = false;
    setLoading(true);
    setLoadError(null);
    Promise.all([getReport(reportId), listCategories()])
      .then(([d, c]) => {
        if (stopped) return;
        setDetail(d);
        setCategories(c.filter((x) => x.active));
      })
      .catch((e) => { if (!stopped) setLoadError(getApiErrorMessage(e, 'Could not load the report.')); })
      .finally(() => { if (!stopped) setLoading(false); });
    return () => { stopped = true; };
  }, [reportId, attempt]);

  useEffect(() => {
    let stopped = false;
    listBranches()
      .then((bs) => { if (!stopped) setBranches(bs.filter((b) => b.is_active !== false)); })
      .catch(() => undefined);
    return () => { stopped = true; };
  }, []);

  if (loading) {
    return <div className="py-12 text-center text-muted-foreground"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;
  }
  if (!detail) {
    return (
      <div className="space-y-4">
        <Button variant="outline" size="sm" onClick={onBack}
                aria-label={t('expenses:buttons.back', { defaultValue: 'Back to expenses' })}>
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <Card>
          <CardContent className="pt-6">
            <div role="alert" className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 mt-0.5 shrink-0 text-warning-strong" />
              <div className="space-y-1 min-w-0">
                <p className="text-sm font-medium text-foreground">
                  {t('expenses:errors.open_failed', { defaultValue: 'This expense report could not be opened.' })}
                </p>
                <p className="text-sm text-muted-foreground break-words">
                  {loadError ?? t('expenses:errors.open_failed_unknown', { defaultValue: 'No report came back.' })}
                </p>
              </div>
            </div>
            <div className="mt-4 flex gap-2 flex-wrap">
              <Button variant="outline" onClick={() => setAttempt((n) => n + 1)}>
                {t('expenses:buttons.retry', { defaultValue: 'Try again' })}
              </Button>
              <Button variant="outline" onClick={onBack}>
                {t('expenses:buttons.back', { defaultValue: 'Back to expenses' })}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }
  const { report, items, receipts, history, reimbursement } = detail;
  // Mirrors expense.Editable. WITHDRAWN is here because changing the report is
  // the whole point of withdrawing it — leaving it out froze the items and left
  // resubmitting the identical report as the only available action.
  const editable = !readOnly
    && (report.status === 'DRAFT' || report.status === 'CHANGES_REQUESTED' || report.status === 'WITHDRAWN');
  const catName = (id: string) => categories.find((c) => c.id === id)?.name || '—';

  const doSubmit = async () => {
    setBusy(true);
    try {
      const res = await submitReport(report.id);
      setFindings(res.findings);
      toast.success(t('expenses:toasts.submitted', { defaultValue: 'Report submitted for approval.' }));
      refresh();
    } catch (e: any) {
      const fs = e?.response?.data?.findings as PolicyFinding[] | undefined;
      if (fs?.length) setFindings(fs);
      toast.error(getApiErrorMessage(e, t('expenses:errors.submit_failed', { defaultValue: 'Could not submit the report.' })));
    } finally {
      setBusy(false);
    }
  };

  // Withdraw and cancel share one dialog and differ only in what they mean, so
  // they share one piece of state. Discard is deliberately NOT in here: it is a
  // plain confirm, because there is no reason to record for something nobody
  // else ever saw.
  const doStop = async ({ reason, note }: { reason?: string; note?: string }) => {
    if (!stopMode) return;
    const cancelling = stopMode === 'cancel';
    setBusy(true);
    try {
      if (cancelling) {
        await cancelReport(report.id, { reason, note });
        toast.success(t('expenses:toasts.cancelled', { defaultValue: 'Report cancelled.' }));
      } else {
        await withdrawReport(report.id, { reason, note });
        toast.success(t('expenses:toasts.withdrawn', { defaultValue: 'Report withdrawn.' }));
      }
      setStopMode(null);
      refresh();
    } catch (e) {
      // The server's refusals name the state and the right action instead
      // ("it is being reviewed right now — ask the reviewer to reject it").
      // Showing our own wording here would throw that away.
      toast.error(getApiErrorMessage(e, cancelling
        ? t('expenses:errors.cancel_failed', { defaultValue: 'Could not cancel the report.' })
        : t('expenses:errors.withdraw_failed', { defaultValue: 'Could not withdraw the report.' })));
    } finally {
      setBusy(false);
    }
  };

  const doDiscard = async () => {
    const ok = await confirm({
      title: t('expenses:discard.title', { defaultValue: 'Discard this draft?' }),
      message: t('expenses:discard.body', {
        // Accurate, because the old copy was not: discarding ARCHIVES the report
        // (archived_at, which every read path filters) and records
        // expense.report.discarded. Nothing is destroyed. Saying "it will simply
        // go away along with its items" described a hard delete that does not
        // happen, and made a reversible, audited action sound irreversible.
        defaultValue:
          'It disappears from your reports. Nobody has seen it, so nothing is sent to anyone — the record is kept for audit and Finance can still retrieve it.',
      }),
      confirmLabel: t('expenses:discard.confirm', { defaultValue: 'Discard draft' }),
      danger: true,
    });
    if (!ok) return;
    setBusy(true);
    try {
      await discardReport(report.id);
      toast.success(t('expenses:toasts.discarded', { defaultValue: 'Draft discarded.' }));
      onBack();
    } catch (e) {
      toast.error(getApiErrorMessage(e, t('expenses:errors.discard_failed', {
        defaultValue: 'Could not discard the draft.',
      })));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Arriving by link at a receipt that cannot be edited would otherwise
          show nothing at all, and the reader would hunt for a button that the
          claim's status has removed. Say which status, rather than stay silent. */}
      {openReceiptId && !editable && (
        <p role="status" className="text-sm rounded-md border border-border bg-muted/40 px-3 py-2 text-muted-foreground">
          {t('expenses:evidence.not_editable', {
            defaultValue:
              'Evidence cannot be edited while this claim is {{status}}. It is editable in draft, withdrawn, or changes-requested.',
            status: report.status.replace(/_/g, ' ').toLowerCase(),
          })}
        </p>
      )}
      <div className="flex items-center gap-3 flex-wrap">
        <Button variant="outline" size="sm" onClick={onBack}
                aria-label={t('expenses:buttons.back', { defaultValue: 'Back to expenses' })}>
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <div className="flex-1 min-w-[200px]">
          <h2 className="text-xl font-bold text-foreground">{report.title}</h2>
          <p className="text-sm text-muted-foreground">{report.reportNumber} · {report.baseTotal} {report.baseCurrency}</p>
        </div>
        <StatusChip status={report.status} />
        {!readOnly && report.status === 'SUBMITTED' && (
          <Button variant="outline" size="sm" onClick={() => setStopMode('withdraw')} disabled={busy}>
            <Undo2 className="w-4 h-4 mr-1" />{t('expenses:buttons.withdraw', { defaultValue: 'Withdraw' })}
          </Button>
        )}
        {/* Discard and Cancel are never both offered: which one applies is a
            property of the report, not a choice for the person to make. A draft
            nobody has seen goes away; anything an approver has seen is kept and
            marked cancelled. */}
        {!readOnly && canDiscardReport(report) && (
          <Button variant="outline" size="sm" onClick={doDiscard} disabled={busy}
            className="text-danger-strong hover:text-danger-strong">
            <Trash2 className="w-4 h-4 mr-1" />{t('expenses:buttons.discard', { defaultValue: 'Discard draft' })}
          </Button>
        )}
        {!readOnly && !canDiscardReport(report) && canCancelReport(report.status) && (
          <Button variant="outline" size="sm" onClick={() => setStopMode('cancel')} disabled={busy}
            className="text-danger-strong hover:text-danger-strong">
            <Ban className="w-4 h-4 mr-1" />{t('expenses:buttons.cancel_report', { defaultValue: 'Cancel report' })}
          </Button>
        )}
        {editable && (
          <Button size="sm" className="bg-orange-500 hover:bg-orange-600 text-white" onClick={doSubmit} disabled={busy || items.length === 0}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
            {t('expenses:buttons.submit_for_approval', { defaultValue: 'Submit for approval' })}
          </Button>
        )}
        {actions?.(detail, refresh)}
      </div>

      {report.status === 'FINANCE_REVIEW' && <FinanceStageNotice stage={detail.financeStage} />}

      <CancelWithReasonDialog
        open={stopMode !== null}
        busy={busy}
        onClose={() => setStopMode(null)}
        onConfirm={doStop}
        reasons={EXPENSE_CANCEL_REASONS.map((r) => ({
          code: r.code,
          label: t(`expenses:cancel_reason.${r.code}`, { defaultValue: r.label }),
        }))}
        // Withdrawing is reversible, so it asks. Cancelling a report an approver
        // already has is not, so it requires — the same test the server applies.
        reasonRequired={stopMode === 'cancel' && cancelReasonRequired(report.status)}
        title={stopMode === 'cancel'
          ? t('expenses:cancel.title', { defaultValue: 'Cancel this report' })
          : t('expenses:withdraw.title', { defaultValue: 'Withdraw this report' })}
        description={stopMode === 'cancel'
          ? t('expenses:cancel.body', {
            defaultValue:
              'The report stays on the record as cancelled — nothing is deleted, and it cannot be submitted again.',
          })
          : t('expenses:withdraw.body', {
            // This used to say "comes back to you as a draft", which was wrong
            // twice over: the report becomes WITHDRAWN, not DRAFT, and until the
            // domain was fixed it could not be edited at all.
            defaultValue:
              'It is pulled out of review and returned to you, where you can change it and submit it again. Saying why is optional.',
          })}
        confirmLabel={stopMode === 'cancel'
          ? t('expenses:buttons.cancel_report', { defaultValue: 'Cancel report' })
          : t('expenses:buttons.withdraw', { defaultValue: 'Withdraw' })}
        keepLabel={t('expenses:buttons.keep', { defaultValue: 'Keep it' })}
        notePlaceholder={t('expenses:cancel.note_ph', { defaultValue: 'e.g. already claimed on EXP-2026-0002' })}
      />

      <FindingsList findings={findings} />

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base">{t('expenses:sections.items', { defaultValue: 'Expense items' })}</CardTitle>
          {editable && !showItemForm && (
            <Button size="sm" variant="outline" onClick={() => setShowItemForm(true)}>
              <Plus className="w-4 h-4 mr-1" />{t('expenses:buttons.add_item', { defaultValue: 'Add expense' })}
            </Button>
          )}
        </CardHeader>
        <CardContent className="space-y-3">
          {showItemForm && (
            <ItemForm
              categories={categories}
              baseCurrency={report.baseCurrency}
              branches={branches}
              onCancel={() => setShowItemForm(false)}
              onSave={async (item, receiptFile) => {
                try {
                  const { id } = await addItem(report.id, item);
                  if (receiptFile) {
                    try {
                      await uploadReceipt(report.id, receiptFile, id);
                    } catch (e) {
                      toast.error(getApiErrorMessage(e, t('expenses:errors.receipt_failed', { defaultValue: 'Item saved, but the receipt failed to upload.' })));
                    }
                  }
                  setShowItemForm(false);
                  refresh();
                } catch (e) {
                  toast.error(getApiErrorMessage(e, t('expenses:errors.item_failed', { defaultValue: 'Could not add the expense.' })));
                }
              }}
            />
          )}
          {items.length === 0 && !showItemForm ? (
            <p className="text-sm text-muted-foreground py-4 text-center">
              {t('expenses:empty.items', { defaultValue: 'No expenses yet — add your first item.' })}
            </p>
          ) : (
            <ul className="divide-y divide-border">
              {items.map((it) => {
                const itemReceipts = receipts.filter((rc) => rc.itemId === it.id);
                return (
                  <li key={it.id} className="py-2.5 flex items-center gap-3 flex-wrap">
                    <div className="flex-1 min-w-[180px]">
                      <p className="text-sm font-medium text-foreground">
                        {catName(it.categoryId)}{it.vendor ? ` · ${it.vendor}` : ''}
                        {readBranchLabel(it.allocation, branches)
                          ? ` · ${readBranchLabel(it.allocation, branches)}`
                          : ''}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {it.txnDate.slice(0, 10)}{it.description ? ` · ${it.description}` : ''}
                      </p>
                    </div>
                    {itemReceipts.map((rc) => (
                      <span key={rc.id} className="inline-flex items-center gap-2">
                        <button type="button"
                          className="inline-flex items-center gap-1 text-xs text-orange-600 hover:underline"
                          onClick={() => receiptDownload(rc.id).then(({ url }) => window.open(url, '_blank'))
                            .catch((e) => toast.error(getApiErrorMessage(e, 'Could not open the receipt.')))}>
                          <Receipt className="w-3.5 h-3.5" />{t('expenses:buttons.receipt', { defaultValue: 'Receipt' })}
                        </button>
                        {editable && (
                          <button type="button" className="text-xs text-muted-foreground hover:underline"
                            onClick={() => setEditingReceipt(editingReceipt === rc.id ? null : rc.id)}>
                            {t('expenses:evidence.edit', { defaultValue: 'Evidence…' })}
                          </button>
                        )}
                      </span>
                    ))}
                    <span className="text-sm font-semibold tabular-nums">{it.amount} {it.currency}</span>
                    {editable && (
                      <button type="button" aria-label={t('expenses:buttons.edit_item', { defaultValue: 'Edit item' })}
                        className="text-muted-foreground hover:text-orange-600"
                        onClick={() => setEditingItem(editingItem === it.id ? null : it.id)}>
                        <Pencil className="w-4 h-4" />
                      </button>
                    )}
                    {editable && (
                      <button type="button" aria-label={t('expenses:buttons.delete_item', { defaultValue: 'Delete item' })}
                        className="text-muted-foreground hover:text-red-600"
                        onClick={() => deleteItem(report.id, it.id).then(refresh)
                          .catch((e) => toast.error(getApiErrorMessage(e, 'Could not delete.')))}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                    {itemReceipts.some((rc) => rc.id === editingReceipt) && (
                      <ReceiptEvidenceEditor receiptId={editingReceipt as string} onDone={() => { setEditingReceipt(null); refresh(); }} />
                    )}
                    {/* Editing in place rather than delete-and-retype: a typo in
                        an amount previously cost the item, its receipt and its
                        evidence, all of which had to be re-entered. */}
                    {editingItem === it.id && (
                      <div className="w-full mt-2">
                        <ItemForm
                          categories={categories}
                          baseCurrency={report.baseCurrency}
                          branches={branches}
                          initial={{
                            categoryId: it.categoryId, txnDate: it.txnDate, vendor: it.vendor,
                            description: it.description, amount: it.amount, currency: it.currency,
                            // Carried so an edit keeps the location — and every
                            // other allocation key — instead of silently
                            // clearing what the form does not show.
                            allocation: it.allocation,
                          }}
                          onCancel={() => setEditingItem(null)}
                          onSave={async (item) => {
                            try {
                              await updateItem(report.id, it.id, item);
                              setEditingItem(null);
                              refresh();
                            } catch (e) {
                              toast.error(getApiErrorMessage(e, t('expenses:errors.item_update_failed', { defaultValue: 'Could not save the change.' })));
                            }
                          }}
                        />
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
          {editable && items.length > 0 && (
            <button type="button" className="text-xs text-muted-foreground hover:underline"
              onClick={async () => {
                const reason = await promptText({ title: t('expenses:evidence.missing_title', { defaultValue: 'Declare a missing receipt' }), label: t('expenses:evidence.missing_prompt', { defaultValue: 'Why is the receipt missing? (required)' }), required: true, multiline: true, confirmLabel: t('expenses:evidence.missing_submit', { defaultValue: 'Declare missing' }) });
                if (reason?.trim()) void declareMissingReceipt(report.id, reason.trim(), items[0].id)
                  .then(() => { toast.success(t('expenses:evidence.missing_saved', { defaultValue: 'Missing-receipt declaration recorded — it will need approval.' })); refresh(); })
                  .catch((e) => toast.error(getApiErrorMessage(e, 'Could not record the declaration.')));
              }}>
              {t('expenses:evidence.missing', { defaultValue: 'I don’t have a receipt — declare it missing' })}
            </button>
          )}
        </CardContent>
      </Card>

      {(reimbursement || history.length > 0) && (
        <Card>
          <CardHeader><CardTitle className="text-base">{t('expenses:sections.timeline', { defaultValue: 'Status timeline' })}</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {reimbursement && (
              <div className="text-sm rounded-lg bg-emerald-50 dark:bg-emerald-500/[0.1] text-emerald-700 dark:text-emerald-300 px-3 py-2">
                {t('expenses:reimbursement.line', {
                  defaultValue: 'Reimbursement {{status}} — {{amount}} {{currency}} via {{method}}',
                  status: reimbursement.status, amount: reimbursement.amount,
                  currency: reimbursement.currency, method: reimbursement.method,
                })}
                {reimbursement.payoutRef ? ` · ${t('expenses:reimbursement.ref', { defaultValue: 'ref' })} ${reimbursement.payoutRef}` : ''}
              </div>
            )}
            <ul className="space-y-1.5">
              {history.map((h, i) => (
                <li key={i} className="text-sm text-muted-foreground flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-orange-400 shrink-0" />
                  <span className="text-foreground font-medium">{t(`expenses:decisions.${h.decision}`, { defaultValue: h.decision.replaceAll('_', ' ') })}</span>
                  {h.reason ? <span>— {h.reason}</span> : null}
                  <span className="ml-auto text-xs">{new Date(h.createdAt).toLocaleString()}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// --- main page ----------------------------------------------------------------

export const ExpensesPage: React.FC = () => {
  const { t } = useTranslation(['expenses']);
  const [reports, setReports] = useState<ExpenseReport[]>([]);
  const [loading, setLoading] = useState(true);
  // A failed list is not an empty one: "No expense reports here yet" over an
  // error tells someone their claims are gone.
  const [listError, setListError] = useState<string | null>(null);
  const [tab, setTab] = useState<'open' | 'history' | 'advances'>('open');
  // The open claim lives in the URL rather than in component state, so a claim
  // (and optionally one receipt's evidence form) can be linked to directly.
  // Back still means back: replace, so it does not stack history entries.
  const [params, setParams] = useSearchParams();
  const selected = params.get('claim');
  const setSelected = useCallback((id: string | null) => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      if (id) next.set('claim', id); else { next.delete('claim'); next.delete('receipt'); }
      return next;
    }, { replace: true });
  }, [setParams]);
  const [creating, setCreating] = useState(false);
  const [newTitle, setNewTitle] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    setListError(null);
    listMyReports()
      .then(setReports)
      .catch((e) => setListError(getApiErrorMessage(e, t('expenses:errors.load_failed', { defaultValue: 'Could not load your expenses.' }))))
      .finally(() => setLoading(false));
  }, [t]);
  useEffect(load, [load]);

  const open = useMemo(() => reports.filter((r) =>
    ['DRAFT', 'SUBMITTED', 'MANAGER_REVIEW', 'FINANCE_REVIEW', 'CHANGES_REQUESTED', 'APPROVED', 'SCHEDULED', 'PAYMENT_FAILED'].includes(r.status)), [reports]);
  const history = useMemo(() => reports.filter((r) => !open.includes(r)), [reports, open]);
  const shown = tab === 'open' ? open : history;

  if (selected) {
    return (
      <ReportDetailView
        reportId={selected}
        openReceiptId={params.get('receipt') ?? undefined}
        onBack={() => { setSelected(null); load(); }}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-foreground">{t('expenses:title', { defaultValue: 'Expenses' })}</h1>
          <p className="text-muted-foreground text-sm">{t('expenses:description', { defaultValue: 'Submit and track your business expenses' })}</p>
        </div>
        <Button className="bg-orange-500 hover:bg-orange-600 text-white" onClick={() => setCreating(true)}>
          <Plus className="w-4 h-4 mr-1" />{t('expenses:buttons.new_report', { defaultValue: 'New expense report' })}
        </Button>
      </div>

      {creating && (
        <Card>
          <CardContent className="pt-6 flex gap-2 flex-wrap items-end">
            <div className="flex-1 min-w-[240px]">
              <span className={label}>{t('expenses:fields.title', { defaultValue: 'Purpose / title' })}</span>
              <input className={input} value={newTitle} onChange={(e) => setNewTitle(e.target.value)}
                placeholder={t('expenses:fields.title_hint', { defaultValue: 'e.g. Dodoma delivery trip' })} />
            </div>
            <Button variant="outline" onClick={() => setCreating(false)}>{t('expenses:buttons.cancel', { defaultValue: 'Cancel' })}</Button>
            <Button className="bg-orange-500 hover:bg-orange-600 text-white" disabled={!newTitle.trim()}
              onClick={() => createReport(newTitle.trim())
                .then((rep) => { setCreating(false); setNewTitle(''); setSelected(rep.id); })
                .catch((e) => toast.error(getApiErrorMessage(e, t('expenses:errors.create_failed', { defaultValue: 'Could not create the report.' }))))}>
              {t('expenses:buttons.create', { defaultValue: 'Create' })}
            </Button>
          </CardContent>
        </Card>
      )}

      <div className="flex gap-1 rounded-full bg-muted p-1 w-fit">
        {(['open', 'history', 'advances'] as const).map((tb) => (
          <button key={tb} type="button" onClick={() => setTab(tb)}
            className={`px-4 py-1.5 rounded-full text-sm font-medium ${tab === tb ? 'bg-background text-foreground shadow' : 'text-muted-foreground'}`}>
            {t(`expenses:tabs.${tb}`, { defaultValue: tb === 'open' ? 'Open' : tb === 'history' ? 'History' : 'Advances' })}
          </button>
        ))}
      </div>

      {tab === 'advances' ? (
        <div className="space-y-4">
          {/* What the 25th notice said, beside what is owed now. */}
          <MyImprestsPanel />
          <AdvancesPanel />
        </div>
      ) : (
      <Card>
        <CardContent className="pt-4">
          {loading ? (
            <div className="py-12 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-muted-foreground" /></div>
          ) : listError ? (
            <div role="alert" className="py-10 text-center space-y-3">
              <AlertTriangle className="w-8 h-8 mx-auto text-warning-strong" />
              <p className="text-sm text-foreground">{listError}</p>
              <Button variant="outline" onClick={load}>{t('expenses:buttons.retry', { defaultValue: 'Try again' })}</Button>
            </div>
          ) : shown.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground">
              <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
              {t('expenses:empty.reports', { defaultValue: 'No expense reports here yet.' })}
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {shown.map((r) => (
                <li key={r.id}>
                  <button type="button" className="w-full py-3 flex items-center gap-3 text-left hover:bg-muted/40 rounded-lg px-2"
                    onClick={() => setSelected(r.id)}>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{r.title}</p>
                      <p className="text-xs text-muted-foreground">{r.reportNumber} · {formatDate(r.createdAt)}</p>
                    </div>
                    <span className="text-sm font-semibold tabular-nums">{r.baseTotal} {r.baseCurrency}</span>
                    <StatusChip status={r.status} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
      )}
    </div>
  );
};

export default ExpensesPage;
